import { Component, ElementRef, computed, inject, OnInit, ViewChild, effect, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10nTranslationModule } from 'angular-l10n';
import { AIService } from '../ai-store/ai.service';
import { SessionService } from '@wonder-core-ng/session-store/session.service';
import { Permission } from '../interfaces/permission.enum';
import { AssistantPersona, ChatAgent, ChatMessage, ComposerAnswer, ConversationSummary, LlmModelOption, ThinkingLevel } from '../ai-store/ai-chat.types';
import { AlertProposalCardComponent } from './alert-proposal-card/alert-proposal-card.component';
import { ComposerQuestionnaireComponent } from './composer-questionnaire/composer-questionnaire.component';
import { MarkdownComponent } from '../markdown/markdown.component';
import { AssistantScreenService } from '../global-assistant/assistant-screen.service';
import { GlobalAssistantService } from '../global-assistant/global-assistant.service';

/** Icon of each agent, on the header and on each of its replies, so the user sees at a glance who is answering. */
const AGENT_ICON: Record<ChatAgent, string> = { orchestrator: 'auto_awesome', builder: 'query_stats', guide: 'travel_explore', composer: 'dashboard_customize', alerts: 'notifications_active' };
const PERSONA_ICON: Record<AssistantPersona, string> = AGENT_ICON;

@Component({
  selector: 'wbi-ai-chat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatTooltipModule,
    L10nTranslationModule,
    MarkdownComponent,
    ComposerQuestionnaireComponent,
    AlertProposalCardComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss',
})
export class AiChatComponent implements OnInit {
  /**
   * The app's single assistant chat (WB-155), rendered inside the global assistant's panel. It shows
   * what the agents say, each reply with its agent's face; the configs they generate go straight into
   * the open builder's form (see `GlobalAssistantService`), not into the chat. Sending goes through
   * the global assistant, which picks the agent.
   */
  protected readonly global = inject(GlobalAssistantService);
  private readonly screens = inject(AssistantScreenService);
  /** Agent of the screen behind the panel; null anywhere else. */
  protected readonly mode = computed(() => this.screens.screen()?.mode ?? null);
  @ViewChild('messageList') private messageListRef!: ElementRef<HTMLElement>;

  protected readonly aiService = inject(AIService);
  private readonly sessionService = inject(SessionService);

  /** Picking the model is an internal affordance: the server refuses the field without this permission. */
  protected readonly canSelectModel = this.sessionService.hasPermission(Permission.AI_MODEL_SELECT);
  private readonly canReadAiMetrics = this.sessionService.hasPermission(Permission.AI_METRICS_READ);
  /** What a turn cost is for whoever manages the AI: the same people who pick the model or read its metrics. */
  protected readonly canSeeCost = computed(() => this.canSelectModel() || this.canReadAiMetrics());
  /** Which assistant is talking (WB-154): it sets the header, the icon, the wording and the colour. */
  protected readonly persona = this.global.persona;
  /**
   * Only to compare models (AI_MODEL_SELECTOR on the server): each agent otherwise runs on its own model.
   * The picked model runs the builder agents; the guide always runs on its own.
   */
  protected readonly showModelPicker = computed(() => this.canSelectModel() && this.aiService.modelSelectorEnabled() && this.persona() !== 'guide');
  protected readonly personaIcon = computed(() => PERSONA_ICON[this.persona()]);
  /** Label of the model in use, for the header trigger. */
  protected readonly selectedModelLabel = computed(() => {
    const selected = this.aiService.selectedModel();
    return this.aiService.availableModels().find(m => m.id === selected)?.label ?? selected ?? '';
  });
  /** Label of the thinking level in use, or the "provider default" wording when none is picked. */
  protected readonly selectedThinkingLabel = computed(() => this.thinkingLabel(this.aiService.selectedThinkingLevel()));
  protected readonly inputControl = new FormControl<string>('', { nonNullable: true });

  constructor() {
    effect(() => {
      // Scroll to bottom when messages update or streaming content grows (a builder's or the orchestrator's)
      this.aiService.messages();
      this.aiService.streamingContent();
      this.global.streamingReply();
      setTimeout(() => this.scrollToBottom(), 50);
    });

    // Who is talking changes the welcome of an untouched chat and the phrases it offers.
    effect(() => {
      const persona = this.persona();
      untracked(() => this.aiService.setPersona(persona));
    });

    // A stopped turn gives its prompt back: it lands in the composer so the user can send it again
    // as it stands, or reword it first. Only when the composer is empty — anything typed while the
    // assistant was answering is the user's newer intent and wins.
    effect(() => {
      const cancelled = this.aiService.cancelledMessage();
      if (cancelled && !this.inputControl.value.trim()) {
        untracked(() => this.inputControl.setValue(cancelled));
      }
    });
    // Same for a stopped orchestrator turn, which the global assistant holds.
    effect(() => {
      const cancelled = this.global.cancelledMessage();
      if (cancelled && !this.inputControl.value.trim()) {
        untracked(() => this.inputControl.setValue(cancelled));
      }
    });
  }

  ngOnInit(): void {
    this.aiService.loadConversations();
    this.aiService.setPersona(this.persona());
    if (this.canSelectModel()) this.aiService.loadModels();
    if (this.aiService.messages().length === 0) {
      this.aiService.addWelcomeMessage();
    }
  }

  /** Stop button, shown in place of send while a turn is in flight: the orchestrator's or a builder's. */
  stop(): void {
    if (this.global.routing()) this.global.cancel(true);
    else if (this.aiService.loading()) this.aiService.cancelMessage();
  }

  send(): void {
    const message = this.inputControl.value.trim();
    if (!message || this.global.busy()) return;
    this.inputControl.setValue('');
    this.global.send(message);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  // ── Session rating ────────────────────────────────────────────────────────

  /** The five stars, as indices, so the template can render them without a pipe. */
  protected readonly stars = [1, 2, 3, 4, 5];

  /** True once there is a session worth scoring: a conversation with something in it. */
  protected canRateSession(): boolean {
    return !!this.aiService.conversationId() && this.aiService.messages().length > 0;
  }

  /** Rating is optional: offered from a small menu in the header, never asked for on leaving the session. */
  rateSession(rating: number): void {
    this.aiService.rateSession(rating);
  }

  clearChat(): void {
    this.global.cancel();
    this.aiService.clearConversation();
  }

  // ── History ───────────────────────────────────────────────────────────────
  toggleHistory(): void {
    this.aiService.toggleHistory();
  }

  newChat(): void {
    this.global.cancel();
    this.aiService.startNewChat();
  }

  openConversation(conversation: ConversationSummary): void {
    this.global.cancel();
    this.aiService.loadConversation(conversation.id);
  }

  isActiveConversation(conversation: ConversationSummary): boolean {
    return conversation.id === this.aiService.conversationId();
  }

  /** Who wrote a reply: the messages of a builder turn, the ones the server stores, carry no mark. */
  agentOf(msg: ChatMessage): ChatAgent {
    return msg.answeredBy ?? 'builder';
  }

  agentIcon(agent: ChatAgent): string {
    return AGENT_ICON[agent];
  }

  isUserMessage(msg: ChatMessage): boolean {
    return msg.role === 'user';
  }

  isToolMessage(msg: ChatMessage): boolean {
    return msg.role === 'tool';
  }

  isThinkingMessage(msg: ChatMessage): boolean {
    return msg.role === 'assistant' && msg.toolCall != null;
  }

  useSuggestion(text: string): void {
    this.global.send(text);
  }

  /** The answers to the composer's questionnaire in `msg` (WB-177 phase 5), sent by the global assistant. */
  answerQuestionnaire(msg: ChatMessage, answers: ComposerAnswer[]): void {
    this.global.answerQuestionnaire(msg.id, answers);
  }

  retry(): void {
    this.aiService.retryMessage();
  }

  selectModel(model: string): void {
    this.aiService.setModel(model);
  }

  /** Pick how hard the model thinks; null hands the decision back to the provider. */
  selectThinkingLevel(level: ThinkingLevel | null): void {
    this.aiService.setThinkingLevel(level);
  }

  /** i18n key naming a level, with its own wording for "whatever the provider does by default". */
  protected thinkingLabel(level: ThinkingLevel | null): string {
    return `$ai.chat.thinking-level.${level ?? 'default'}`;
  }

  /** Day of the conversation, so a benchmark run can be told apart at a glance. */
  protected formatConversationDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /**
   * Price and context window of a model for the picker, as `$in / $out · 128K`. Deliberately
   * symbol-only so it reads the same in every locale; the tooltip carries the wording. Returns null
   * when nothing is configured, so the option shows just its name.
   */
  protected modelMeta(model: LlmModelOption): string | null {
    const { inputPricePerMillionTokens: input, outputPricePerMillionTokens: output, contextWindowTokens: context } = model;
    const parts: string[] = [];

    if (input !== undefined || output !== undefined) {
      parts.push(`${this.price(input)} / ${this.price(output)}`);
    }
    if (context !== undefined) parts.push(this.tokens(context));

    return parts.length ? parts.join(' · ') : null;
  }

  /** Gateway prices carry the provider's full precision (0.859908); three significant digits is plenty here. */
  private price(value: number | undefined): string {
    return value === undefined ? '—' : `$${Number(value.toPrecision(3))}`;
  }

  private tokens(value: number): string {
    if (value >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
    if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
    return `${value}`;
  }

  /** A model outside the configured catalog is a locally served one (llama-server, Docker Models). */
  isLocalModel(model: string): boolean {
    const catalog = this.aiService.availableModels();
    if (catalog.length) return !catalog.some(m => m.id === model);
    return !model.toLowerCase().includes('mistral');
  }

  /** USD with four decimals for the sub-dollar turns most of them are, two above a dollar. */
  static formatCost(usd: number): string {
    if (!usd) return '$0';
    return usd >= 1 ? `$${usd.toFixed(2)}` : `$${usd.toFixed(4)}`;
  }

  protected formatCost(usd: number): string {
    return AiChatComponent.formatCost(usd);
  }

  formatResponseTime(ms: number): string {
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
  }

  private scrollToBottom(): void {
    const el = this.messageListRef?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
